import React, { useRef, useState } from 'react';
import { updateAssignmentAsStudent, uploadAssignmentPhoto } from './api';
import { BLOCK_COLOR } from './blockTheme';

const STATUS_CYCLE = ['not_started', 'in_progress', 'complete'];
const STATUS_LABEL = { not_started: 'Not started', in_progress: 'In progress', complete: 'Done', flagged: 'Flagged' };

// "Math or a writing assignment" (gap-review item 8) — a block/course
// heuristic, not a per-assignment field, since it applies uniformly and
// needs no retrofit of the seed data. Composition is deliberately excluded:
// per the real year plan, composition is parent-scribed (the parent writes,
// not the student), so there's no student handwriting artifact to
// photograph there — cursive copywork is the one that's genuinely
// independent student handwriting.
const PHOTO_REQUIRED_BLOCKS = ['math'];
const PHOTO_REQUIRED_COURSES = ['cursive'];

function needsPhoto(a) {
  return PHOTO_REQUIRED_BLOCKS.includes(a.block) || PHOTO_REQUIRED_COURSES.includes(a.courseId);
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 12.5 9.5 18 20 6.5" />
    </svg>
  );
}

// Shared assignment row for the student views (Today/Week/Flagged/Board) —
// owns its own writes (status cycling, flagging, photo upload) so that
// logic isn't duplicated per page. Day Board-themed (see dayBoardTheme.css);
// only ever rendered inside .day-board-theme. Collapsed shows title/course/
// status; expanded surfaces subLabel/resourceLink.
//
// `itemRef`, `leading` and `style` exist for the week board, which needs these
// rows to be draggable. The card itself knows nothing about drag-and-drop: the
// board passes a grip element as `leading` (it carries the drag listeners) and
// the measured node ref as `itemRef`. Confining activation to that grip is what
// keeps dragging from fighting the card's own controls — a check button, an
// expanding body, three toggles, a textarea and a file input, all of which
// would compete with listeners spread across the whole row.
export default function AssignmentCard({ a, course, familyId, interactive = true, itemRef, leading, style }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPhotoPrompt, setShowPhotoPrompt] = useState(false);
  // null = the flag box is closed; a string = it's open holding that draft.
  // Kept separate from `open` so flagging doesn't force the detail panel out.
  const [flagDraft, setFlagDraft] = useState(null);
  const fileInput = useRef(null);
  const hasDetail = Boolean(a.subLabel || a.resourceLink || a.studentQuestion);
  const complete = a.status === 'complete';
  const flagged = a.status === 'flagged' || a.confusionFlag;
  const bc = BLOCK_COLOR[a.block] || 'var(--ink-3)';

  function cycleStatus() {
    const i = STATUS_CYCLE.indexOf(a.status);
    const next = i === -1 ? 'in_progress' : STATUS_CYCLE[(i + 1) % STATUS_CYCLE.length];
    if (next === 'complete' && needsPhoto(a) && !a.screenshotURL) {
      setShowPhotoPrompt(true);
      return;
    }
    const patch = { status: next };
    if (flagged) { patch.confusionFlag = false; patch.studentQuestion = null; }
    if (next === 'complete') patch.completedAt = new Date().toISOString();
    updateAssignmentAsStudent(familyId, a.id, patch);
  }

  // Flagging asks for context before it writes, rather than firing on the tap:
  // "I'm stuck on the second page" is far more use to a parent than a bare
  // flag, and it's the moment the student actually knows what's wrong. The
  // note is optional — Flag it with an empty box behaves exactly as the old
  // one-tap flag did. Reused to edit the note on an already-flagged item.
  function openFlagPrompt() {
    setFlagDraft(a.studentQuestion || '');
  }

  function submitFlag() {
    const question = (flagDraft || '').trim();
    updateAssignmentAsStudent(familyId, a.id, {
      status: 'flagged',
      confusionFlag: true,
      studentQuestion: question || null,
    });
    setFlagDraft(null);
  }

  // Unflagging lands on 'in_progress' — the same place the check-circle's
  // cycle already put a flagged item, just reachable now from a labelled
  // button on any view the card renders in (Today, This week, Board, Flagged)
  // instead of only by discovering the checkmark.
  function unflag() {
    updateAssignmentAsStudent(familyId, a.id, {
      status: 'in_progress',
      confusionFlag: false,
      studentQuestion: null,
    });
    setFlagDraft(null);
  }

  async function handlePhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadAssignmentPhoto(familyId, a.id, file);
      await updateAssignmentAsStudent(familyId, a.id, {
        status: 'complete',
        completedAt: new Date().toISOString(),
        screenshotURL: url,
        screenshotUploadedAt: new Date().toISOString(),
      });
      setShowPhotoPrompt(false);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  function completeWithoutPhoto() {
    updateAssignmentAsStudent(familyId, a.id, { status: 'complete', completedAt: new Date().toISOString() });
    setShowPhotoPrompt(false);
  }

  return (
    <li ref={itemRef} className={`db-item ${complete ? 'db-item--complete' : ''}`} style={{ '--bc': bc, flexWrap: 'wrap', ...style }}>
      {leading}
      {interactive ? (
        <button className="db-check" aria-pressed={complete} onClick={cycleStatus} title={complete ? 'Mark not done' : 'Mark done'}>
          <CheckIcon />
        </button>
      ) : (
        <span className="db-check" aria-pressed={complete} style={{ cursor: 'default' }}><CheckIcon /></span>
      )}

      <div className="db-item-body" style={{ cursor: hasDetail ? 'pointer' : 'default' }} onClick={() => hasDetail && setOpen(o => !o)}>
        <div className="db-item-title">{a.title}</div>
        <div className="db-item-sub">
          {[course?.name, STATUS_LABEL[a.status], a.screenshotURL && '📷'].filter(Boolean).join(' · ')}
        </div>
      </div>

      <div className="db-item-meta">
        {flagged && <span className="db-flag-dot" title="Flagged" />}
        {a.estMinutes ? <span className="db-mins">{a.estMinutes}m</span> : null}
        {interactive && !flagged && (
          <button className="db-detail-toggle" onClick={openFlagPrompt} title="I'm stuck">🚩</button>
        )}
        {interactive && flagged && (
          <>
            <button className="db-detail-toggle" onClick={openFlagPrompt} title="Add or change what's wrong">✎</button>
            <button className="db-detail-toggle" onClick={unflag} title="Not stuck any more — unflag">🚩✕</button>
          </>
        )}
        {hasDetail && (
          <button className="db-detail-toggle" onClick={() => setOpen(o => !o)}>{open ? '▲' : '▼'}</button>
        )}
      </div>

      {flagDraft !== null && (
        <div className="db-detail-panel" style={{ width: '100%', flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          <label htmlFor={`q-${a.id}`}>What's the question or problem? (optional)</label>
          <textarea
            id={`q-${a.id}`}
            className="input"
            rows={2}
            autoFocus
            value={flagDraft}
            placeholder="e.g. I don't get problem 4 — can we look at it together?"
            onChange={e => setFlagDraft(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn--small btn--primary" onClick={submitFlag}>
              {flagged ? 'Save' : 'Flag it'}
            </button>
            <button className="db-detail-toggle" style={{ padding: 0 }} onClick={() => setFlagDraft(null)}>
              Cancel
            </button>
            {!flagged && (
              <button className="db-detail-toggle" style={{ padding: 0 }} onClick={submitFlag}>
                Skip the note
              </button>
            )}
          </div>
        </div>
      )}

      {open && hasDetail && (
        <div className="db-detail-panel" style={{ width: '100%' }}>
          {a.subLabel && <div>{a.subLabel}</div>}
          {a.resourceLink && <a href={a.resourceLink} target="_blank" rel="noreferrer">Open resource ↗</a>}
          {a.studentQuestion && <div style={{ color: 'var(--bad)' }}>Question: {a.studentQuestion}</div>}
        </div>
      )}

      {showPhotoPrompt && (
        <div className="db-detail-panel" style={{ width: '100%', alignItems: 'flex-start' }}>
          <span>Take a photo of your work for this one:</span>
          <input ref={fileInput} type="file" accept="image/*" capture="environment" onChange={handlePhoto} disabled={uploading} />
          {uploading && <span className="db-item-sub">Uploading…</span>}
          <button className="db-detail-toggle" style={{ padding: 0 }} onClick={completeWithoutPhoto} disabled={uploading}>
            Skip for now
          </button>
        </div>
      )}
    </li>
  );
}

export { STATUS_CYCLE, STATUS_LABEL };
